import React from 'react';
import { withStyles, WithStyles } from '@mui/styles';
import type { Theme } from '@mui/material/styles';
import {
  Grid,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Save as SaveIcon,
  ExpandMore as ExpandMoreIcon,
  Group as GroupIcon,
  Settings as SettingsIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import CommunityService from '../../services/community-service';
import type { Community } from '@stash/shared-types';
import { toProperCase } from '../../utils/string-util';

const COMMUNITY_PRIVACY = {
  OPEN: 'open',
  INVITE_ONLY: 'inviteOnly',
  ADMIN_APPROVAL_REQUIRED: 'adminApprovalRequired',
} as const;

const COMMUNITY_PRIVACY_DESCRIPTIONS: Record<string, string> = {
  OPEN: 'Open - Anyone can join',
  INVITE_ONLY: 'Invite Only - Members only by invitation',
  ADMIN_APPROVAL_REQUIRED: 'Admin Approval Required - Requires admin approval to join',
};

const styles = (theme: Theme) => ({
  root: {
    padding: theme.spacing(1),
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(2),
    },
  },
  responsiveHeader: {
    fontSize: '1.1rem',
    [theme.breakpoints.up('md')]: {
      fontSize: '1.25rem',
    },
  },
  section: {
    marginBottom: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
      marginBottom: theme.spacing(3),
    },
  },
  sectionHeader: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  submitButton: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  accordion: {
    marginBottom: theme.spacing(1),
    [theme.breakpoints.up('md')]: {
      marginBottom: theme.spacing(2),
    },
  },
  textField: {
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.up('md')]: {
      marginBottom: theme.spacing(2),
    },
  },
  multilineField: {
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.up('md')]: {
      marginBottom: theme.spacing(2),
    },
  },
});

interface WebLink {
  label: string;
  url: string;
}

interface CommunityData {
  recordID: string;
  name?: string;
  description?: string;
  privacy?: string;
  webLinks?: WebLink[];
}

interface FormData {
  name: string;
  description: string;
  privacy: string;
  webLinks: WebLink[];
}

interface ExpandedSections {
  basic: boolean;
  privacy: boolean;
  links: boolean;
}

interface State {
  formData: FormData;
  loading: boolean;
  error: string | null;
  success: boolean;
  expandedSections: ExpandedSections;
}

interface Props extends WithStyles<typeof styles> {
  community: CommunityData;
  onCommunityUpdate?: (community: unknown) => void;
}

class CommunityEditForm extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      formData: this.initializeFormData(props.community),
      loading: false,
      error: null,
      success: false,
      expandedSections: {
        basic: true,
        privacy: false,
        links: false,
      },
    };
  }

  initializeFormData = (community: CommunityData | null): FormData => ({
    name: community?.name ?? '',
    description: community?.description ?? '',
    privacy: community?.privacy ?? 'open',
    webLinks: community?.webLinks ?? [],
  });

  override componentDidUpdate(prevProps: Props): void {
    if (prevProps.community !== this.props.community) {
      this.setState({
        formData: this.initializeFormData(this.props.community),
      });
    }
  }

  handleInputChange =
    (field: keyof FormData) =>
    (event: { target: { value: string } }): void => {
      const value = event.target.value;
      this.setState((prevState) => ({
        formData: {
          ...prevState.formData,
          [field]: value,
        },
      }));
    };

  handleWebLinkChange =
    (index: number, field: keyof WebLink) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
      const value = event.target.value;
      this.setState((prevState) => ({
        formData: {
          ...prevState.formData,
          webLinks: prevState.formData.webLinks.map((link, i) =>
            i === index ? { ...link, [field]: value } : link,
          ),
        },
      }));
    };

  handleAddWebLink = (): void => {
    this.setState((prevState) => ({
      formData: {
        ...prevState.formData,
        webLinks: [...prevState.formData.webLinks, { label: '', url: '' }],
      },
    }));
  };

  handleRemoveWebLink = (index: number): void => {
    this.setState((prevState) => ({
      formData: {
        ...prevState.formData,
        webLinks: prevState.formData.webLinks.filter((_, i) => i !== index),
      },
    }));
  };

  handleAccordionChange =
    (panel: keyof ExpandedSections) =>
    (_event: React.SyntheticEvent, isExpanded: boolean): void => {
      this.setState((prevState) => ({
        expandedSections: {
          ...prevState.expandedSections,
          [panel]: isExpanded,
        },
      }));
    };

  handleSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();

    this.setState({ loading: true, error: null, success: false });

    try {
      const communityService = new CommunityService();
      const { community } = this.props;
      const { formData } = this.state;

      const cleanedWebLinks = formData.webLinks.filter((link) => link.url && link.url.trim());

      const updateData = {
        ...formData,
        webLinks: cleanedWebLinks,
      };

      const updatedCommunity = await communityService.updateCommunity(
        community.recordID,
        updateData as unknown as Partial<Community>,
      );

      console.log('Updated community:', updatedCommunity);

      this.setState({
        loading: false,
        success: true,
        error: null,
      });

      if (this.props.onCommunityUpdate) {
        this.props.onCommunityUpdate(updatedCommunity);
      }

      setTimeout(() => {
        this.setState({ success: false });
      }, 3000);
    } catch (error) {
      console.error('Error updating community:', error);
      const message = error instanceof Error ? error.message : String(error);
      this.setState({
        loading: false,
        error: `Failed to update community: ${message}`,
        success: false,
      });
    }
  };

  override render(): React.JSX.Element {
    const { classes, community } = this.props;
    const { formData, loading, error, success, expandedSections } = this.state;

    if (!community) {
      return <Alert severity="warning">No community data available for editing.</Alert>;
    }

    return (
      <form onSubmit={this.handleSubmit} className={classes.root}>
        {error && (
          <Alert severity="error" sx={{ marginBottom: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ marginBottom: 2 }}>
            Community information updated successfully!
          </Alert>
        )}

        <Accordion
          expanded={expandedSections.basic}
          onChange={this.handleAccordionChange('basic')}
          className={classes.accordion}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography
              variant="h6"
              className={`${classes.sectionHeader} ${classes.responsiveHeader}`}
            >
              <GroupIcon sx={{ fontSize: 'inherit' }} />
              Basic Information
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={{ xs: 2, md: 3 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Community Name"
                  value={formData.name}
                  onChange={this.handleInputChange('name') as React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>}
                  className={classes.textField}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Community Description"
                  value={formData.description}
                  onChange={this.handleInputChange('description') as React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>}
                  className={classes.multilineField}
                  multiline
                  rows={4}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        <Accordion
          expanded={expandedSections.privacy}
          onChange={this.handleAccordionChange('privacy')}
          className={classes.accordion}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography
              variant="h6"
              className={`${classes.sectionHeader} ${classes.responsiveHeader}`}
            >
              <SettingsIcon sx={{ fontSize: 'inherit' }} />
              Privacy Settings
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={{ xs: 2, md: 3 }}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Privacy Level</InputLabel>
                  <Select
                    value={formData.privacy}
                    onChange={this.handleInputChange('privacy') as (event: { target: { value: string } }) => void}
                    label="Privacy Level"
                  >
                    {Object.entries(COMMUNITY_PRIVACY).map(([key, value]) => (
                      <MenuItem key={value} value={value}>
                        {COMMUNITY_PRIVACY_DESCRIPTIONS[key] ?? toProperCase(value)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        <Accordion
          expanded={expandedSections.links}
          onChange={this.handleAccordionChange('links')}
          className={classes.accordion}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography
              variant="h6"
              className={`${classes.sectionHeader} ${classes.responsiveHeader}`}
            >
              <LinkIcon sx={{ fontSize: 'inherit' }} />
              Web Links
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                External Links
              </Typography>
              {formData.webLinks.map((link, index) => (
                <Box key={index} display="flex" alignItems="center" gap={2} mb={2}>
                  <TextField
                    sx={{ flex: 2 }}
                    label="Link Label"
                    value={link.label}
                    onChange={this.handleWebLinkChange(index, 'label')}
                    placeholder="Website, Events, Facebook, etc."
                  />
                  <TextField
                    sx={{ flex: 3 }}
                    label="URL"
                    value={link.url}
                    onChange={this.handleWebLinkChange(index, 'url')}
                    type="url"
                    placeholder="https://example.com"
                  />
                  <Button
                    color="error"
                    onClick={() => this.handleRemoveWebLink(index)}
                    variant="outlined"
                    size="small"
                  >
                    Remove
                  </Button>
                </Box>
              ))}
              <Button
                variant="outlined"
                color="primary"
                onClick={this.handleAddWebLink}
                size="small"
              >
                Add Link
              </Button>
            </Box>
          </AccordionDetails>
        </Accordion>

        <Button
          type="submit"
          variant="contained"
          color="primary"
          size="large"
          className={classes.submitButton}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
        >
          {loading ? 'Updating...' : 'Update Community'}
        </Button>
      </form>
    );
  }
}

export default withStyles(styles)(CommunityEditForm);
